/**
 * Asset Purchase Section
 *
 * Capex modelling: an asset bought with finance (deposit + amortised
 * repayments) against an ongoing monthly benefit, e.g. a local AI
 * server reducing cloud AI spend. Each card shows the net monthly
 * profit effect "while financed" and, via the toggle, the effect
 * "after payoff" (benefit with no repayment). Deposit = 100% models
 * an outright purchase.
 */

import { useState, useCallback } from 'react';
import type { FormEvent } from 'react';
import { useAssetPurchases } from '@/hooks/useAssetPurchases';
import { useProfitScenarios } from '@/hooks/useProfitScenarios';
import { CalcTooltip } from './CalcTooltip';
import { formatCurrency } from '@/utils/formatters';
import type {
  AssetPurchaseComputed,
  AssetPurchaseInput,
} from '@/types/assetPurchase';
import './AssetPurchaseSection.css';

type PayoffView = 'financed' | 'paidoff';

const EMPTY_FORM = {
  name: '',
  purchasePrice: '',
  depositPercent: '20',
  interestRatePercent: '8',
  termMonths: '36',
  monthlyBenefit: '',
  benefitNote: '',
  startMonth: '',
};

function effectLabel(effect: number): string {
  return `${effect >= 0 ? '+' : ''}${formatCurrency(effect)}`;
}

export function AssetPurchaseSection() {
  const {
    data, loading, error, mutating, mutateError,
    createAsset, updateAsset, deleteAsset,
  } = useAssetPurchases();
  const { data: scenarios } = useProfitScenarios();

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  /** Per-card preview toggle: while financed vs after payoff */
  const [payoffView, setPayoffView] = useState<Record<string, PayoffView>>({});

  const baselineNpbt = scenarios?.baseline.result.npbtMonthly ?? null;

  const startEdit = useCallback((asset: AssetPurchaseComputed) => {
    setEditingId(asset.id);
    setForm({
      name: asset.name,
      purchasePrice: String(asset.purchasePrice),
      depositPercent: String(asset.depositPercent),
      interestRatePercent: String(asset.interestRatePercent),
      termMonths: String(asset.termMonths),
      monthlyBenefit: String(asset.monthlyBenefit),
      benefitNote: asset.benefitNote ?? '',
      startMonth: asset.startMonth,
    });
    setFormVisible(true);
    setConfirmingDeleteId(null);
  }, []);

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormVisible(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const input: AssetPurchaseInput = {
        name: form.name.trim(),
        purchasePrice: Number(form.purchasePrice),
        depositPercent: Number(form.depositPercent),
        interestRatePercent: Number(form.interestRatePercent),
        termMonths: Number(form.termMonths),
        monthlyBenefit: Number(form.monthlyBenefit),
        benefitNote: form.benefitNote.trim() === '' ? undefined : form.benefitNote.trim(),
        startMonth: form.startMonth === '' ? undefined : form.startMonth,
      };
      const ok = editingId
        ? await updateAsset(editingId, input)
        : await createAsset(input);
      if (ok) resetForm();
    },
    [form, editingId, createAsset, updateAsset, resetForm]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setConfirmingDeleteId(null);
      const ok = await deleteAsset(id);
      if (ok && editingId === id) resetForm();
    },
    [deleteAsset, editingId, resetForm]
  );

  const toggleView = useCallback((id: string, view: PayoffView) => {
    setPayoffView((prev) => ({ ...prev, [id]: view }));
  }, []);

  const assets = data?.assets ?? [];

  return (
    <section className="rt-section ap-section" aria-label="Asset purchases">
      <div className="ap-title-row">
        <h3 className="rt-section__title">
          Asset purchases
          <CalcTooltip title="Asset purchase effect">
            <p>
              Each asset is modelled as financed principal (price less deposit)
              repaid monthly over the term at the given rate. The monthly
              benefit is the ongoing cost saving the asset delivers — e.g. a
              local AI server reducing cloud AI spend.
            </p>
            <p>
              <strong>While financed:</strong> benefit minus repayment.{' '}
              <strong>After payoff:</strong> the full benefit, no repayment.
              A 100% deposit models an outright purchase.
            </p>
          </CalcTooltip>
        </h3>
        <button
          onClick={() => (formVisible ? resetForm() : setFormVisible(true))}
          className="ap-add"
          type="button"
        >
          {formVisible ? 'Cancel' : '+ Add asset'}
        </button>
      </div>
      <p className="rt-section__note">
        Model a capital purchase and its monthly profit effect — while it is
        being paid off, and once it is fully paid.
      </p>

      {error && <p className="ap-error" role="alert">{error}</p>}

      {formVisible && (
        <form onSubmit={handleSubmit} className="ap-form">
          <div className="ap-form__grid">
            <label className="rt-field">
              <span className="rt-field__label">Asset name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.replace(/[<>]/g, '') }))}
                maxLength={120}
                required
                className="rt-field__input"
                placeholder="e.g. AI inference server"
              />
            </label>
            <label className="rt-field">
              <span className="rt-field__label">Purchase price ($)</span>
              <input
                type="number"
                min="1"
                max="100000000"
                step="1000"
                value={form.purchasePrice}
                onChange={(e) => setForm((f) => ({ ...f, purchasePrice: e.target.value }))}
                required
                className="rt-field__input"
                placeholder="200000"
              />
            </label>
            <label className="rt-field">
              <span className="rt-field__label">Deposit (%)</span>
              <input
                type="number"
                min="0"
                max="100"
                step="5"
                value={form.depositPercent}
                onChange={(e) => setForm((f) => ({ ...f, depositPercent: e.target.value }))}
                required
                className="rt-field__input"
              />
              <span className="rt-field__hint">100% = bought outright</span>
            </label>
            <label className="rt-field">
              <span className="rt-field__label">Interest rate (% p.a.)</span>
              <input
                type="number"
                min="0"
                max="50"
                step="0.25"
                value={form.interestRatePercent}
                onChange={(e) => setForm((f) => ({ ...f, interestRatePercent: e.target.value }))}
                required
                className="rt-field__input"
              />
            </label>
            <label className="rt-field">
              <span className="rt-field__label">Term (months)</span>
              <input
                type="number"
                min="1"
                max="360"
                step="1"
                value={form.termMonths}
                onChange={(e) => setForm((f) => ({ ...f, termMonths: e.target.value }))}
                required
                className="rt-field__input"
              />
            </label>
            <label className="rt-field">
              <span className="rt-field__label">Monthly benefit ($)</span>
              <input
                type="number"
                min="0"
                max="10000000"
                step="100"
                value={form.monthlyBenefit}
                onChange={(e) => setForm((f) => ({ ...f, monthlyBenefit: e.target.value }))}
                required
                className="rt-field__input"
                placeholder="e.g. 4000 cloud AI saved"
              />
            </label>
            <label className="rt-field">
              <span className="rt-field__label">Benefit note</span>
              <input
                type="text"
                value={form.benefitNote}
                onChange={(e) => setForm((f) => ({ ...f, benefitNote: e.target.value.replace(/[<>]/g, '') }))}
                maxLength={300}
                className="rt-field__input"
                placeholder="What the saving replaces"
              />
            </label>
            <label className="rt-field">
              <span className="rt-field__label">First repayment month</span>
              <input
                type="month"
                value={form.startMonth}
                onChange={(e) => setForm((f) => ({ ...f, startMonth: e.target.value }))}
                className="rt-field__input"
              />
              <span className="rt-field__hint">Defaults to the current month</span>
            </label>
          </div>
          <div className="ap-form__actions">
            <button type="submit" disabled={mutating} className="rt-settings__save">
              {mutating ? 'Saving...' : editingId ? 'Save changes' : 'Add asset'}
            </button>
            <button type="button" onClick={resetForm} className="ap-cancel">
              Cancel
            </button>
            {mutateError && (
              <span className="ap-error" role="alert">{mutateError}</span>
            )}
          </div>
        </form>
      )}

      {loading && assets.length === 0 && (
        <p className="ap-muted">Loading assets...</p>
      )}

      {!loading && assets.length === 0 && !formVisible && (
        <p className="ap-muted">
          No assets modelled yet. Add one to see its financed and post-payoff
          effect on monthly profit.
        </p>
      )}

      {assets.length > 0 && (
        <div className="ap-cards">
          {assets.map((a) => {
            const view: PayoffView =
              payoffView[a.id] ?? (a.financedNow ? 'financed' : 'paidoff');
            const effect =
              view === 'financed'
                ? a.netMonthlyEffectNow
                : a.netMonthlyEffectAfterPayoff;
            const withAsset =
              baselineNpbt !== null ? baselineNpbt + effect : null;
            return (
              <article key={a.id} className="ap-card" aria-label={`Asset ${a.name}`}>
                <header className="ap-card__head">
                  <div>
                    <h4 className="ap-card__name">{a.name}</h4>
                    <p className="ap-card__price">
                      {formatCurrency(a.purchasePrice)}
                      {a.principal > 0
                        ? ` — ${formatCurrency(a.principal)} financed over ${a.termMonths} mo @ ${a.interestRatePercent}%`
                        : ' — bought outright'}
                    </p>
                  </div>
                  <div className="ap-card__actions">
                    <button
                      onClick={() => startEdit(a)}
                      className="ps-action-btn"
                      type="button"
                      aria-label={`Edit ${a.name}`}
                    >
                      Edit
                    </button>
                    {confirmingDeleteId === a.id ? (
                      <>
                        <button
                          onClick={() => { void handleDelete(a.id); }}
                          className="ps-action-btn ps-action-btn--danger"
                          type="button"
                          aria-label={`Confirm delete of ${a.name}`}
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmingDeleteId(null)}
                          className="ps-action-btn"
                          type="button"
                          aria-label="Cancel delete"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setConfirmingDeleteId(a.id)}
                        className="ps-action-btn ps-action-btn--danger"
                        type="button"
                        aria-label={`Delete ${a.name}`}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </header>

                <dl className="ap-card__facts">
                  {a.principal > 0 && (
                    <>
                      <div>
                        <dt>Repayment</dt>
                        <dd>{formatCurrency(a.monthlyRepayment)}/mo</dd>
                      </div>
                      <div>
                        <dt>Interest over term</dt>
                        <dd>{formatCurrency(a.totalInterest)}</dd>
                      </div>
                      <div>
                        <dt>Term remaining</dt>
                        <dd>
                          {a.monthsRemaining > 0
                            ? `${a.monthsRemaining} of ${a.termMonths} mo`
                            : 'Paid off'}
                        </dd>
                      </div>
                    </>
                  )}
                  <div>
                    <dt>Monthly benefit</dt>
                    <dd>
                      +{formatCurrency(a.monthlyBenefit)}
                      {a.benefitNote ? ` (${a.benefitNote})` : ''}
                    </dd>
                  </div>
                </dl>

                {/* Financed vs paid-off preview toggle */}
                <div
                  className="ap-toggle"
                  role="group"
                  aria-label={`Profit effect view for ${a.name}`}
                >
                  <button
                    type="button"
                    className={`ap-toggle__btn ${view === 'financed' ? 'ap-toggle__btn--active' : ''}`}
                    onClick={() => toggleView(a.id, 'financed')}
                    aria-pressed={view === 'financed'}
                  >
                    While financed
                  </button>
                  <button
                    type="button"
                    className={`ap-toggle__btn ${view === 'paidoff' ? 'ap-toggle__btn--active' : ''}`}
                    onClick={() => toggleView(a.id, 'paidoff')}
                    aria-pressed={view === 'paidoff'}
                  >
                    After payoff
                  </button>
                </div>

                <p className={`ap-card__effect ${effect >= 0 ? 'ap-positive' : 'ap-negative'}`}>
                  {effectLabel(effect)}/mo{' '}
                  {view === 'financed' ? 'while financed' : 'after payoff'}
                </p>
                {withAsset !== null && (
                  <p className="ap-card__npbt">
                    Baseline NPBT {formatCurrency(baselineNpbt ?? 0)}/mo →{' '}
                    <strong>{formatCurrency(withAsset)}/mo</strong> with this asset
                    ({formatCurrency((withAsset - (baselineNpbt ?? 0)) * 3)} over
                    the quarter)
                  </p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
